import { KeyRound, Settings, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  type Adesao,
  type Instalacao,
  type Licenca,
  NaoAutorizado,
  type Situacao,
} from "./api";
import { Casca, type ItemDeMenu, Marca, Rodape, useTema } from "./casca";
import type { Preferencia, Tema } from "./tema";
import { atencaoDe, resumir, sinalDe, type Venda } from "../domain/crm";
import { estaAtrasada, maisNova, paraMostrar } from "../version/semver";
import {
  Botao,
  Campo,
  Cartao,
  classeEntrada,
  cn,
  Dica,
  quandoFoi,
  Selo,
  Vazio,
} from "./ui";

// O console administrativo, vestindo a MESMA casca do painel do cliente (`src/client/casca.tsx`):
// cabeçalho com a marca, menu que vira fileira no celular, e rodapé com a razão social. Eram duas
// telas do mesmo produto com desenhos diferentes, e a divergência começava na primeira correção.
//
// O que ele existe para responder, nessa ordem: quem está pagando, o que cada um está rodando, e
// quem está atrasado. Operar por `curl` respondia a primeira pergunta e nenhuma das outras.
//
// ⚠️ O cabeçalho NÃO tem e-mail nem "Sair", ao contrário do painel: a sessão do console é um token
// administrativo, não uma pessoa — o cookie não guarda identidade porque não há identidade a
// guardar (`src/api/admin-auth.ts`). No lugar vai a versão corrente do produto.

const LOCALES = [
  { id: "pt-BR", nome: "Português" },
  { id: "en", nome: "English" },
  { id: "es", nome: "Español" },
] as const;

function Entrar({ aoEntrar, tema }: { aoEntrar: () => void; tema: Tema }) {
  const [token, setToken] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [indo, setIndo] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setIndo(true);
    setErro(null);
    try {
      await api.entrar(token);
      aoEntrar();
    } catch {
      // A mensagem não distingue "token errado" de "token de outra instalação": quem está tentando
      // adivinhar não ganha pista nenhuma com isso.
      setErro("Token não aceito.");
    } finally {
      setIndo(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-6">
      <form onSubmit={enviar} className="w-full max-w-sm">
        <Marca tema={tema} className="mx-auto mb-6 h-9" />
        <Cartao className="flex flex-col gap-4">
          <div>
            <h1 className="text-lg font-semibold">Hub de Licença</h1>
            <p className="mt-1 text-sm text-text-muted">Console administrativo.</p>
          </div>
          <Campo rotulo="Token administrativo">
            <input
              type="password"
              autoFocus
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className={classeEntrada}
              placeholder="••••••••"
            />
          </Campo>
          {erro ? <p className="text-sm text-error">{erro}</p> : null}
          <Botao type="submit" variante="primario" carregando={indo} desabilitado={!token}>
            Entrar
          </Botao>
        </Cartao>
        <Rodape className="mt-6" />
      </form>
    </div>
  );
}

// Os produtos, suas faixas e a UNIDADE de cada um. "ilimitado" vira cota nula — 0 seria lido como
// cota de verdade por qualquer comparação numérica.
//
// 🔴 A unidade não é enfeite: o iDagent conta TENANTS QUE EXISTEM e o Chatwoot conta CONTAS COM O
// KANBAN LIGADO. São coisas diferentes, e uma tela que chamasse as duas de "tenants" ensinaria a
// pessoa errado na hora de vender.
//
// Mora no módulo porque criar e MUDAR de faixa (#20) precisam da mesma lista: duas cópias divergem no
// dia em que uma faixa nova entrar.
const PRODUTOS = {
  "idagent-pro": {
    nome: "iDagent Pro",
    unidade: ["tenant", "tenants"],
    faixas: ["1", "10", "30", "ilimitado"],
    dica: "Conta todo tenant que existe, não só os ativos.",
  },
  "chatwoot-pro": {
    nome: "Chatwoot Pro",
    unidade: ["conta", "contas"],
    faixas: ["1", "3", "ilimitado"],
    dica: "Conta as contas com o Kanban ligado, e usuários são ilimitados em todas as faixas.",
  },
} as const;

type Produto = keyof typeof PRODUTOS;

// Licença antiga, ou produto que o console ainda não conhece, cai no iDagent em vez de quebrar a
// tela: o pior que acontece é a unidade aparecer errada numa linha.
const produtoDe = (p: string): Produto =>
  p in PRODUTOS ? (p as Produto) : "idagent-pro";

const cotaDaFaixa = (f: string): number | null =>
  f === "ilimitado" ? null : Number(f);

const faixaDaCota = (cota: number | null): string =>
  cota === null ? "ilimitado" : String(cota);

const nomeDaFaixa = (produto: string, f: string): string => {
  if (f === "ilimitado") return "Ilimitado";
  const [um, varios] = PRODUTOS[produtoDe(produto)].unidade;
  return `${f} ${f === "1" ? um : varios}`;
};

function CriarLicenca({ aoCriar }: { aoCriar: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [cliente, setCliente] = useState("");
  const [produto, setProduto] = useState<Produto>("idagent-pro");
  const [plano, setPlano] = useState("10");
  const [indo, setIndo] = useState(false);

  // Trocar de produto troca as faixas, então a escolhida pode não existir mais na lista nova.
  function escolherProduto(p: Produto) {
    setProduto(p);
    if (!PRODUTOS[p].faixas.includes(plano as never)) {
      setPlano(PRODUTOS[p].faixas[0] as string);
    }
  }

  async function criar() {
    setIndo(true);
    try {
      await api.criarLicenca({
        product: produto,
        plan: plano,
        tenant_limit: cotaDaFaixa(plano),
        customer: cliente.trim() || undefined,
      });
      setCliente("");
      setAberto(false);
      aoCriar();
    } finally {
      setIndo(false);
    }
  }

  if (!aberto) {
    return (
      <Botao variante="primario" onClick={() => setAberto(true)}>
        Nova licença
      </Botao>
    );
  }
  return (
    <Cartao className="flex flex-col gap-3">
      <Campo rotulo="Cliente">
        <input
          autoFocus
          value={cliente}
          onChange={(e) => setCliente(e.target.value)}
          className={classeEntrada}
          placeholder="Nome de quem vai usar"
        />
      </Campo>
      <Campo rotulo="Produto">
        <select
          value={produto}
          onChange={(e) => escolherProduto(e.target.value as Produto)}
          className={classeEntrada}
        >
          {Object.entries(PRODUTOS).map(([id, p]) => (
            <option key={id} value={id}>
              {p.nome}
            </option>
          ))}
        </select>
      </Campo>
      <Campo rotulo="Faixa" dica={PRODUTOS[produto].dica}>
        <select value={plano} onChange={(e) => setPlano(e.target.value)} className={classeEntrada}>
          {PRODUTOS[produto].faixas.map((f) => (
            <option key={f} value={f}>
              {nomeDaFaixa(produto, f)}
            </option>
          ))}
        </select>
      </Campo>
      <div className="flex gap-2">
        <Botao variante="primario" onClick={criar} carregando={indo}>
          Criar
        </Botao>
        <Botao onClick={() => setAberto(false)}>Cancelar</Botao>
      </div>
    </Cartao>
  );
}

// ⚠️ A ORDEM das três importa na leitura: é a sequência do que o cliente pode ter, do menos para o
// mais. E a dica de cada uma diz o que ELE recebe, não o nome interno do modo.
const SITUACOES: Array<{ id: Situacao; rotulo: string; dica: string }> = [
  {
    id: "nova",
    rotulo: "Nada ainda",
    dica: "Um comando. O cliente roda no servidor dele e o resto acontece.",
  },
  {
    id: "adotar",
    rotulo: "Já roda a versão paga",
    dica: "A instalação já existe. Estas duas variáveis no painel dela, e ela adere no próximo contato com o hub.",
  },
  {
    id: "migrar",
    rotulo: "Roda a versão gratuita",
    dica: "Primeiro a imagem, depois as variáveis — nessa ordem. Na edição gratuita as variáveis são ignoradas, então colá-las antes não faria nada.",
  },
];

function LinkDeInstalacao({ licenca }: { licenca: Licenca }) {
  const [adesao, setAdesao] = useState<Adesao | null>(null);
  const [locale, setLocale] = useState<string>("pt-BR");
  const [indo, setIndo] = useState(false);
  const [copiado, setCopiado] = useState(false);

  // 🔴 O PRODUTO dá o padrão; quem opera diz o que o cliente JÁ TEM. Antes isto era decidido só
  // pelo produto, e não havia como dizer "a instalação já existe" para o iDagent — foi o que me fez
  // gravar duas variáveis à mão no painel de uma instalação nossa (#46).
  //
  // ⚠️ Aqui NÃO se usa `produtoDe`: aquele apelido cai no iDagent quando o produto é desconhecido, o
  // que é bom para a unidade de uma linha e péssimo aqui — entregaria o instalador do iDagent a um
  // produto que ninguém conhece.
  const [situacao, setSituacao] = useState<Situacao>(
    licenca.product === "idagent-pro" ? "nova" : "adotar",
  );
  const porScript = (adesao?.situacao ?? situacao) === "nova" && licenca.product === "idagent-pro";

  async function gerar() {
    setIndo(true);
    try {
      setAdesao(
        await api.gerarAdesao(licenca.id, {
          situacao,
          ...(situacao === "nova" ? { locale } : {}),
        }),
      );
      setCopiado(false);
    } finally {
      setIndo(false);
    }
  }

  async function copiar() {
    if (!adesao) return;
    // O que se copia é o que aquele produto precisa colar. Nunca os dois.
    // ⚠️ Na migração o botão copia as VARIÁVEIS, não o `docker login`: o login tem a senha, e um
    // clique que põe senha na área de transferência sem a pessoa pedir é surpresa ruim.
    const texto = adesao.command ?? adesao.env;
    if (!texto) return;
    await navigator.clipboard.writeText(texto);
    setCopiado(true);
  }

  return (
    <Cartao className="flex flex-col gap-3">
      <div>
        <h3 className="font-medium">Adesão</h3>
        <p className="mt-1 text-sm text-text-muted">
          {SITUACOES.find((x) => x.id === (adesao?.situacao ?? situacao))?.dica}
        </p>
      </div>

      {/* 🔴 A pergunta que faltava. O produto decide o padrão; quem opera sabe o que o cliente tem. */}
      {/* 🔴 NÃO é `Campo`: ele renderiza um <label>, e um <label> em volta de botões faz o clique no
          TÍTULO acionar o primeiro botão — aqui, escolher "Nada ainda" sem ninguém querer. Um
          `<span>` com `role="group"` mantém o agrupamento para leitor de tela sem esse efeito. */}
      <div role="group" aria-labelledby="rotulo-situacao" className="flex flex-col gap-1.5">
        <span id="rotulo-situacao" className="text-sm text-text-secondary">
          O que o cliente já tem
        </span>
        <div className="flex flex-wrap gap-1">
          {SITUACOES.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => {
                setSituacao(x.id);
                setAdesao(null);
              }}
              aria-pressed={situacao === x.id}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                situacao === x.id
                  ? "border-accent bg-bg-tertiary text-text-primary"
                  : "border-border text-text-secondary hover:border-border-hover",
              )}
            >
              {x.rotulo}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-2">
        {porScript ? (
          <Campo rotulo="Idioma">
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className={classeEntrada}
            >
              {LOCALES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.nome}
                </option>
              ))}
            </select>
          </Campo>
        ) : null}
        <Botao variante="primario" onClick={gerar} carregando={indo}>
          {adesao ? "Gerar outro" : porScript ? "Gerar link" : "Gerar código"}
        </Botao>
      </div>

      {adesao ? (
        <div className="flex flex-col gap-2">
          {/* 🔴 A IMAGEM VEM ANTES, e a ordem é a regra, não a diagramação: numa edição gratuita as
              duas variáveis são ignoradas de propósito. Colá-las primeiro não dá erro — não faz
              NADA, e a pessoa fica esperando por algo que nunca vai acontecer. */}
          {adesao.registro ? (
            <div className="flex flex-col gap-2 rounded-lg border border-warning/40 p-3">
              <p className="font-medium text-sm">1. Trocar a imagem</p>
              <p className="text-text-muted text-xs">
                Entre no registro e troque a imagem do serviço. Só depois cole as variáveis.
              </p>
              <pre className="overflow-x-auto rounded-lg border border-border bg-bg-primary p-3 font-mono text-text-secondary text-xs">
                {`docker login ${adesao.registro.host} -u '${adesao.registro.usuario}' --password-stdin <<'EOF'\n${adesao.registro.senha}\nEOF\n\ndocker pull ${adesao.registro.imagem}`}
              </pre>
              {/* ⚠️ Dito aqui porque é aqui que a pessoa decide se copia agora ou depois. */}
              <p className="text-warning text-xs">
                Esta senha aparece uma única vez. Gerar outra adesão apaga esta credencial.
              </p>
              <p className="mt-1 font-medium text-sm">2. Colar as variáveis</p>
            </div>
          ) : null}
          <pre className="overflow-x-auto rounded-lg border border-border bg-bg-primary p-3 font-mono text-xs text-text-secondary">
            {adesao.command ?? adesao.env}
          </pre>
          <div className="flex items-center gap-3">
            <Botao onClick={copiar}>
              {copiado ? "Copiado" : porScript ? "Copiar comando" : "Copiar variáveis"}
            </Botao>
            <span className="text-xs text-text-muted">
              Vale até {new Date(adesao.expires_at).toLocaleString("pt-BR")}
            </span>
          </div>
          {/* O prazo não é do link por capricho: é o tempo até a instalação subir. E o que ele
              carrega, se vazar, é uma credencial só de leitura que vence em dois dias. */}
          {porScript ? (
            <p className="text-xs text-text-muted">
              O link carrega uma credencial descartável, só de download, que vence sozinha. A
              credencial definitiva nasce quando a instalação falar com o hub pela primeira vez.
            </p>
          ) : adesao.registro ? (
            <p className="text-xs text-text-muted">
              Depois de trocar a imagem E colar as variáveis, reinicie. 🔴 Reiniciar só com as
              variáveis não adianta: a edição gratuita não as lê.
            </p>
          ) : (
            <p className="text-xs text-text-muted">
              Depois de colar, reinicie a instalação. 🔴 Sem o código ela continua subindo — só que
              sem licença, e sem licença o Kanban responde 404.
            </p>
          )}
        </div>
      ) : null}
    </Cartao>
  );
}

function Instalacoes({
  licenca,
  versaoAtual,
}: {
  licenca: Licenca;
  versaoAtual: string | null;
}) {
  const [linhas, setLinhas] = useState<Instalacao[] | null>(null);
  const [revogando, setRevogando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLinhas(await api.instalacoes(licenca.id));
  }, [licenca.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function revogar(id: string) {
    setRevogando(id);
    try {
      await api.revogarInstalacao(id);
      await carregar();
    } finally {
      setRevogando(null);
    }
  }

  if (!linhas) return <Vazio>Carregando…</Vazio>;
  if (linhas.length === 0) {
    return <Vazio>Nenhuma instalação ainda. Gere o link acima e envie ao cliente.</Vazio>;
  }

  return (
    <div className="flex flex-col gap-2">
      {linhas.map((i) => {
        const atrasada = estaAtrasada(i.last_version, versaoAtual);
        return (
          <Cartao key={i.id} className={cn("flex flex-col gap-2", i.revoked_at && "opacity-60")}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-mono text-sm">{i.id}</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                  <span>{paraMostrar(i.last_version, "versão desconhecida")}</span>
                  <span>·</span>
                  <span>{quandoFoi(i.last_seen_at)}</span>
                  {i.last_tenant_count !== null ? (
                    <>
                      <span>·</span>
                      <span>
                        {i.last_tenant_count} tenant{i.last_tenant_count === 1 ? "" : "s"}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
              {i.revoked_at ? (
                <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-xs text-text-muted">
                  revogada
                </span>
              ) : (
                <Botao
                  variante="perigo"
                  onClick={() => revogar(i.id)}
                  carregando={revogando === i.id}
                >
                  Revogar
                </Botao>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Copiado deles, e melhorado: lá este selo aparece para o CLIENTE. Aqui aparece para
                  quem vende, que é quem precisa saber qual cliente ficou para trás. */}
              {atrasada ? (
                <span className="rounded-full border border-warning/40 px-2 py-0.5 text-xs text-warning">
                  atrasada — {paraMostrar(versaoAtual)} disponível
                </span>
              ) : null}
              {i.robot_name ? (
                <span className="rounded-full border border-border px-2 py-0.5 text-xs text-text-muted">
                  credencial ativa
                  {i.credential_expires_at
                    ? ` até ${new Date(i.credential_expires_at).toLocaleDateString("pt-BR")}`
                    : ""}
                </span>
              ) : (
                <span className="rounded-full border border-border px-2 py-0.5 text-xs text-text-muted">
                  sem credencial de registro
                </span>
              )}
            </div>
          </Cartao>
        );
      })}
    </div>
  );
}

// Emitir token para quem não alcança o hub (#21).
//
// Fica recolhido atrás de um link porque é a exceção, não o caminho: o normal é o cliente rodar o
// comando e a instalação se cadastrar sozinha. Uma tela que mostra os dois com o mesmo peso ensina
// errado.
function TokenManual({ licenca }: { licenca: Licenca }) {
  const [aberto, setAberto] = useState(false);
  const [instalacao, setInstalacao] = useState("");
  const [dias, setDias] = useState(365);
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{
    token: string;
    expires_at: string;
  } | null>(null);
  const [copiado, setCopiado] = useState(false);

  async function emitir() {
    setIndo(true);
    setErro(null);
    try {
      setResultado(
        await api.emitirToken(licenca.id, {
          installation_id: instalacao.trim(),
          days: dias,
        }),
      );
      setCopiado(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não deu");
    } finally {
      setIndo(false);
    }
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="self-start text-xs text-text-muted hover:text-text-secondary hover:underline"
      >
        A instalação não alcança o hub?
      </button>
    );
  }

  return (
    <Cartao className="flex flex-col gap-3">
      <div>
        <h3 className="font-medium">Token para instalação sem acesso ao hub</h3>
        <p className="mt-1 text-sm text-text-muted">
          Rede fechada, firewall que não vai abrir, ou versão anterior à que sincroniza. O cliente
          cola este token na tela de licença dele.
        </p>
      </div>
      <Campo
        rotulo="Identificador da instalação"
        dica="O cliente encontra na tela de Licença do produto."
      >
        <input
          autoFocus
          value={instalacao}
          onChange={(e) => setInstalacao(e.target.value)}
          className={classeEntrada}
          placeholder="00000000-0000-0000-0000-000000000000"
        />
      </Campo>
      <Campo
        rotulo="Validade"
        dica="Não há renovação atrás deste token: quando vencer, alguém emite outro à mão."
      >
        <select
          value={dias}
          onChange={(e) => setDias(Number(e.target.value))}
          className={classeEntrada}
        >
          {[90, 180, 365].map((d) => (
            <option key={d} value={d}>
              {d} dias
            </option>
          ))}
        </select>
      </Campo>
      <div className="flex gap-2">
        <Botao
          variante="primario"
          onClick={emitir}
          carregando={indo}
          desabilitado={instalacao.trim().length === 0}
        >
          Emitir
        </Botao>
        <Botao onClick={() => setAberto(false)}>Fechar</Botao>
      </div>
      {erro ? <p className="text-xs text-error">{erro}</p> : null}
      {resultado ? (
        <div className="flex flex-col gap-2">
          <pre className="overflow-x-auto break-all whitespace-pre-wrap rounded-lg border border-border bg-bg-primary p-3 font-mono text-xs text-text-secondary">
            {resultado.token}
          </pre>
          <div className="flex items-center gap-3">
            <Botao
              onClick={async () => {
                await navigator.clipboard.writeText(resultado.token);
                setCopiado(true);
              }}
            >
              {copiado ? "Copiado" : "Copiar token"}
            </Botao>
            <span className="text-xs text-text-muted">
              Vence em {new Date(resultado.expires_at).toLocaleDateString("pt-BR")}
            </span>
          </div>
        </div>
      ) : null}
    </Cartao>
  );
}

// Mudar a faixa de quem trocou de plano (#20).
//
// 🔴 A frase sobre "próximo contato" não é enfeite. O hub não alcança a instalação: é ela que
// pergunta, a cada 6 h, e o token que ela recebe carrega a cota lida naquele momento. Sem dizer
// isso, quem clicou fica olhando a tela do cliente esperando mudar agora.
function MudarFaixa({
  licenca,
  aoFechar,
  aoMudar,
}: {
  licenca: Licenca;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const [faixa, setFaixa] = useState(faixaDaCota(licenca.tenant_limit));
  const [indo, setIndo] = useState(false);
  const mudou = cotaDaFaixa(faixa) !== licenca.tenant_limit;

  async function salvar() {
    setIndo(true);
    try {
      await api.alterarLicenca(licenca.id, {
        plan: faixa,
        tenant_limit: cotaDaFaixa(faixa),
      });
      aoMudar();
      aoFechar();
    } finally {
      setIndo(false);
    }
  }

  return (
    <Cartao className="flex flex-col gap-3">
      <Campo
        rotulo="Nova faixa"
        dica="Vale a partir do próximo contato da instalação, em até 6 h."
      >
        <select
          value={faixa}
          onChange={(e) => setFaixa(e.target.value)}
          className={classeEntrada}
        >
          {PRODUTOS[produtoDe(licenca.product)].faixas.map((f) => (
            <option key={f} value={f}>
              {nomeDaFaixa(licenca.product, f)}
            </option>
          ))}
        </select>
      </Campo>
      {/* Descer de faixa é seguro (14/09: acima da cota não desliga nada, só bloqueia criar), e
          dizer isso aqui é o que permite atender um downgrade sem medo. */}
      {mudou && cotaDaFaixa(faixa) !== null && licenca.tenant_limit === null ? (
        <p className="text-xs text-text-muted">
          Reduzir não desliga nada: tenants que já existem continuam, e só a criação de novos passa a
          ser bloqueada.
        </p>
      ) : null}
      <div className="flex gap-2">
        <Botao variante="primario" onClick={salvar} carregando={indo} desabilitado={!mudou}>
          Salvar
        </Botao>
        <Botao onClick={aoFechar}>Cancelar</Botao>
      </div>
    </Cartao>
  );
}


// Os dois ids do Stripe, colados por quem cunha a licenca. Decisao do dono (21/10): e a mesma mao,
// no mesmo lugar onde ele ja define o dono — e um campo colado e explicito, enquanto casar pelo
// e-mail do customer falharia calado no cliente que paga com outro endereco.
//
// 🔴 Os dois NAO sao a mesma coisa, e a tela precisa dizer isso: o `cus_` e do CLIENTE (abre o
// portal de cobranca, e vale para TODAS as licencas dele) e o `sub_` e DESTA licenca (e por ele que
// o webhook sabe o que mudar). Um cliente com Chatwoot Pro e iDagent Pro tem um `cus_` e dois
// `sub_`.
// O erro cru nao serve aqui. Os tres que esta tela pode produzir tem a MESMA causa provavel — colar
// o id errado — e dizer qual deles aconteceu sem dizer o que fazer deixa o dono olhando um codigo.
const ERROS_DO_STRIPE: Record<string, string> = {
  id_do_stripe_invalido:
    "Esse id nao tem a forma certa. O do cliente comeca com cus_ e o da assinatura com sub_ — confira se nao trocou os dois campos.",
  licenca_sem_dono:
    "Defina o dono primeiro: o id do cliente fica na conta dele, e esta licenca ainda nao tem uma.",
  id_do_stripe_em_uso:
    "Esse id ja esta ligado a outra licenca. Confira se nao colou o de outro cliente.",
};

function Stripe({ licenca, aoMudar }: { licenca: Licenca; aoMudar: () => void }) {
  const [editando, setEditando] = useState(false);
  const [cus, setCus] = useState("");
  const [sub, setSub] = useState("");
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setIndo(true);
    setErro(null);
    try {
      // Em sequencia, e nao em paralelo: sao dois pedidos independentes, e se o segundo falhar o
      // dono precisa saber qual dos dois entrou. Um `Promise.all` que rejeita esconde isso.
      if (cus.trim() !== (licenca.stripe_customer_id ?? "")) {
        await api.ligarClienteAoStripe(licenca.id, cus.trim());
      }
      if (sub.trim() !== (licenca.stripe_subscription_id ?? "")) {
        await api.ligarLicencaAoStripe(licenca.id, sub.trim());
      }
      setEditando(false);
      aoMudar();
    } catch (e) {
      const codigo = e instanceof Error ? e.message : "";
      setErro(ERROS_DO_STRIPE[codigo] ?? codigo ?? "nao deu");
    } finally {
      setIndo(false);
    }
  }

  if (!editando) {
    const ligado = licenca.stripe_customer_id && licenca.stripe_subscription_id;
    return (
      <Cartao className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-text-muted text-xs">Stripe</p>
          {ligado ? (
            <p className="mt-0.5 truncate font-mono text-sm">
              {licenca.stripe_customer_id} · {licenca.stripe_subscription_id}
            </p>
          ) : (
            // Diz a CONSEQUENCIA, nao o estado: "sem id" nao significa nada para quem olha a tela
            // com pressa, e a consequencia e que o pagamento nao chega na licenca.
            <p className="mt-0.5 text-sm text-warning">
              {licenca.stripe_customer_id || licenca.stripe_subscription_id
                ? "Ligacao pela metade — o pagamento nao vai mexer nesta licenca."
                : "Sem ligacao — nada que acontecer no Stripe chega nesta licenca."}
            </p>
          )}
        </div>
        <Botao
          onClick={() => {
            setCus(licenca.stripe_customer_id ?? "");
            setSub(licenca.stripe_subscription_id ?? "");
            setEditando(true);
          }}
        >
          {ligado ? "Trocar" : "Ligar ao Stripe"}
        </Botao>
      </Cartao>
    );
  }

  return (
    <Cartao className="flex flex-col gap-3">
      <Campo
        rotulo="Cliente no Stripe (cus_...)"
        dica="E do CLIENTE, nao desta licenca: vale para todas as licencas dele, e e o que abre o portal de cobranca."
      >
        <input
          value={cus}
          onChange={(e) => setCus(e.target.value)}
          placeholder="cus_..."
          className={`${classeEntrada} font-mono`}
        />
      </Campo>
      <Campo
        rotulo="Assinatura desta licenca (sub_...)"
        dica="E por ele que o webhook sabe qual licenca mudar quando o pagamento falha ou o cliente cancela."
      >
        <input
          value={sub}
          onChange={(e) => setSub(e.target.value)}
          placeholder="sub_..."
          className={`${classeEntrada} font-mono`}
        />
      </Campo>
      {erro ? <p className="text-error text-sm">{erro}</p> : null}
      <div className="flex gap-2">
        <Botao
          variante="primario"
          onClick={() => void salvar()}
          carregando={indo}
        >
          Salvar
        </Botao>
        <Botao onClick={() => setEditando(false)}>Cancelar</Botao>
      </div>
    </Cartao>
  );
}

// Quem VE esta licenca no painel do cliente. E outra coisa do que o nome digitado na criacao: aquele
// e uma etiqueta nossa, este e uma conta com e-mail que abre a porta.
//
// 🔴 Sem esta tela a licenca nascia invisivel para quem comprou, e liga-la exigia `curl` com o token
// administrativo — no primeiro minuto do cliente com o produto (#67).
function Dono({
  licenca,
  aoMudar,
}: {
  licenca: Licenca;
  aoMudar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setIndo(true);
    setErro(null);
    try {
      await api.definirDono(licenca.id, {
        email: email.trim(),
        ...(nome.trim() ? { name: nome.trim() } : {}),
      });
      setEditando(false);
      setEmail("");
      setNome("");
      aoMudar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não deu");
    } finally {
      setIndo(false);
    }
  }

  if (!editando) {
    return (
      <Cartao className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-text-muted text-xs">Dono</p>
          {licenca.customer_email ? (
            <p className="mt-0.5 truncate text-sm">{licenca.customer_email}</p>
          ) : (
            // Nao e um detalhe a preencher depois: sem dono, quem comprou abre o painel e nao ve
            // nada. Por isso o aviso tem a cor de alerta e diz a CONSEQUENCIA, nao o estado.
            <p className="mt-0.5 text-sm text-warning">
              Sem dono — ninguém vê esta licença no painel do cliente.
            </p>
          )}
        </div>
        <Botao
          onClick={() => {
            setEmail(licenca.customer_email ?? "");
            setEditando(true);
          }}
        >
          {licenca.customer_email ? "Trocar" : "Definir dono"}
        </Botao>
      </Cartao>
    );
  }

  return (
    <Cartao className="flex flex-col gap-3">
      <Campo
        rotulo="E-mail do cliente"
        dica="É por ele que o cliente entra no painel, pelo código ou pelo Google."
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={classeEntrada}
          placeholder="cliente@empresa.com.br"
        />
      </Campo>
      <Campo rotulo="Nome (opcional)">
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className={classeEntrada}
          placeholder="Como ele se chama"
        />
      </Campo>

      {/* ⚠️ A consequencia dita em voz alta, porque ela nao se desfaz sozinha: um e-mail errado NAO
          da erro — cria um cliente novo e vazio, e a licenca some do painel de quem deveria ve-la. */}
      <p className="text-text-muted text-xs">
        {licenca.customer_email
          ? `Isto move a licença para outro painel: ${licenca.customer_email} deixa de vê-la.`
          : "Um e-mail errado não dá erro: cria um cliente vazio, e a licença fica invisível para quem comprou."}
      </p>

      {erro ? <p className="text-error text-sm">{erro}</p> : null}

      <div className="flex gap-2">
        <Botao
          variante="primario"
          onClick={() => void salvar()}
          carregando={indo}
          desabilitado={!email.includes("@")}
        >
          Salvar
        </Botao>
        <Botao onClick={() => setEditando(false)}>Cancelar</Botao>
      </div>
    </Cartao>
  );
}

function Detalhe({
  licenca,
  versaoAtual,
  aoMudar,
}: {
  licenca: Licenca;
  versaoAtual: string | null;
  aoMudar: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [indo, setIndo] = useState(false);
  const [mudandoFaixa, setMudandoFaixa] = useState(false);

  async function cancelar() {
    setIndo(true);
    try {
      await api.cancelarLicenca(licenca.id);
      setConfirmando(false);
      aoMudar();
    } finally {
      setIndo(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{licenca.customer ?? "Sem nome"}</h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-text-muted">
            <span className="font-mono text-xs">{licenca.id}</span>
            <Selo estado={licenca.status} />
          </div>
        </div>
        {licenca.status !== "canceled" ? (
          confirmando ? (
            <div className="flex items-center gap-2">
              {/* Cancelar não desliga ninguém agora — a renovação é que para. Dizer isso aqui evita
                  a hesitação de quem teme derrubar um cliente no meio do expediente. */}
              <span className="text-xs text-text-muted">
                A renovação para; o que está rodando continua até vencer.
              </span>
              <Botao variante="perigo" onClick={cancelar} carregando={indo}>
                Confirmar
              </Botao>
              <Botao onClick={() => setConfirmando(false)}>Não</Botao>
            </div>
          ) : (
            <Botao variante="perigo" onClick={() => setConfirmando(true)}>
              Cancelar licença
            </Botao>
          )
        ) : null}
      </div>

      <Cartao className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <p className="text-xs text-text-muted">Faixa</p>
          <div className="mt-0.5 flex items-center gap-2">
            <p className="text-sm">
              {nomeDaFaixa(licenca.product, faixaDaCota(licenca.tenant_limit))}
            </p>
            {licenca.status !== "canceled" && !mudandoFaixa ? (
              <button
                type="button"
                onClick={() => setMudandoFaixa(true)}
                className="text-xs text-accent hover:underline"
              >
                mudar
              </button>
            ) : null}
          </div>
        </div>
        {[
          ["Instalações", String(licenca.installations)],
          ["Versão", paraMostrar(maisNova(licenca.versions))],
          ["Último contato", quandoFoi(licenca.last_seen_at)],
        ].map(([rotulo, valor]) => (
          <div key={rotulo}>
            <p className="text-xs text-text-muted">{rotulo}</p>
            <p className="mt-0.5 text-sm">{valor}</p>
          </div>
        ))}
      </Cartao>

      <Dono licenca={licenca} aoMudar={aoMudar} />

      <Stripe licenca={licenca} aoMudar={aoMudar} />

      {mudandoFaixa ? (
        <MudarFaixa
          licenca={licenca}
          aoFechar={() => setMudandoFaixa(false)}
          aoMudar={aoMudar}
        />
      ) : null}

      {licenca.status !== "canceled" ? (
        <>
          <LinkDeInstalacao licenca={licenca} />
          <TokenManual licenca={licenca} />
        </>
      ) : null}

      <div>
        <h3 className="mb-2 font-medium">Instalações</h3>
        <Instalacoes licenca={licenca} versaoAtual={versaoAtual} />
      </div>
    </div>
  );
}

// As configurações do painel (#25). Hoje só o tema — e só isso mesmo: um menu de configurações com
// um item só já seria exagero, e com itens que não fazem nada seria promessa.
// —— O CRM ————————————————————————————————————————————————————————————————————

// 🔴 O rotulo e o que o DONO diz, nao o que o sistema deduz. Deduzir "vendida" de ter instalacao
// poria toda licenca de teste no faturamento — e foi essa a decisao de 19/10 (#62).
const VENDAS: Array<{ id: Venda; rotulo: string; classe: string }> = [
  { id: "sold", rotulo: "Vendida", classe: "text-success border-success/40" },
  { id: "trial", rotulo: "Teste", classe: "text-text-muted border-border" },
  { id: "courtesy", rotulo: "Cortesia", classe: "text-accent border-accent/40" },
];

// Grava na hora, sem passo de confirmar: sao tres opcoes, e reverter e escolher de novo. O que
// protege nao e um "tem certeza?", e o historico — a mudanca entra em `license_changes`, entao
// "desde quando esse cliente e pagante?" tem resposta meses depois.
function MudarVenda({
  licenca,
  aoMudar,
}: {
  licenca: Licenca;
  aoMudar: () => void;
}) {
  const [indo, setIndo] = useState(false);

  async function escolher(v: Venda) {
    if (v === licenca.sale) return;
    setIndo(true);
    try {
      await api.alterarLicenca(licenca.id, { sale: v });
      aoMudar();
    } finally {
      setIndo(false);
    }
  }

  return (
    <select
      value={licenca.sale}
      disabled={indo || licenca.status === "canceled"}
      onChange={(e) => void escolher(e.target.value as Venda)}
      aria-label="O que esta licença é"
      className={cn(classeEntrada, "w-auto py-1 text-xs")}
    >
      {VENDAS.map((v) => (
        <option key={v.id} value={v.id}>
          {v.rotulo}
        </option>
      ))}
    </select>
  );
}

// ON ou OFF primeiro, porque e o que se le de relance num painel: esta de pe, ou nao esta.
//
// ⚠️ Mas o detalhe NAO some junto. "Nunca instalou" e "parou" viram os dois OFF, e sao coisas
// diferentes no negocio: uma e venda que ainda nao chegou ao servidor, a outra e cliente que
// parou — e e por isso que os alertas tratam uma e ignoram a outra. Juntar as duas num OFF seco
// apagaria a diferenca justo na tela que existe para mostra-la.
function Sinal({ ultimo, agora }: { ultimo: string | null; agora: Date }) {
  const s = sinalDe(ultimo ? new Date(ultimo) : null, agora);
  const ligado = s === "viva";
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span
        className={cn(
          "rounded-full border px-2 py-0.5 font-medium text-xs",
          ligado
            ? "border-success/40 text-success"
            : "border-border text-text-muted",
        )}
      >
        {ligado ? "ON" : "OFF"}
      </span>
      {/* ⚠️ Dois simbolos, dois significados: `!` e algo que deu errado (parou de falar), `?` e algo
          que nao se sabe (nunca apareceu). Um simbolo so para os dois devolveria o problema que o
          OFF ja tinha — juntar cliente que sumiu com venda que nao chegou ao servidor. */}
      {s === "sumida" ? (
        <Dica texto={`Parou de falar com o hub ${quandoFoi(ultimo)}`} />
      ) : null}
      {s === "nunca" ? (
        <Dica tom="duvida" texto="Nunca falou com o hub: ainda não instalou." />
      ) : null}
    </span>
  );
}

function Numero({
  rotulo,
  valor,
  tom,
  nota,
}: {
  rotulo: string;
  valor: number;
  tom?: "alerta";
  nota?: string;
}) {
  return (
    <Cartao>
      <p className="text-text-muted text-xs">{rotulo}</p>
      <p
        className={cn(
          "mt-1 font-semibold text-2xl",
          tom === "alerta" && valor > 0 && "text-warning",
        )}
      >
        {valor}
      </p>
      {nota ? <p className="mt-1 text-text-muted text-xs">{nota}</p> : null}
    </Cartao>
  );
}

function PaginaCRM({
  licencas,
  aoMudar,
}: {
  licencas: Licenca[] | null;
  aoMudar: () => void;
}) {
  // Uma unica leitura do relogio para a pagina inteira: duas chamadas a `new Date()` durante a
  // mesma renderizacao poem uma licenca de um lado da borda no resumo e do outro na tabela.
  // biome-ignore lint/correctness/useExhaustiveDependencies: o relogio e relido quando os dados sao
  const agora = useMemo(() => new Date(), [licencas]);

  if (!licencas) return <Vazio>Carregando…</Vazio>;
  if (licencas.length === 0) return <Vazio>Nenhuma licença ainda.</Vazio>;

  // Os dois lados andam JUNTOS num par, em vez de duas listas casadas por indice: indice casado e
  // o jeito de, no dia em que uma das listas for filtrada, a tela passar a mostrar o alerta de uma
  // licenca ao lado do nome de outra.
  const pares = licencas.map((l) => ({
    l,
    regra: {
      venda: l.sale,
      status: l.status,
      ultimoContato: l.last_seen_at ? new Date(l.last_seen_at) : null,
    },
  }));
  const r = resumir(
    pares.map((x) => x.regra),
    agora,
  );

  // 🔴 O valor do CRM esta aqui: quem aparece numa coluna e nao na outra. Por isso as duas listas
  // vem ANTES da tabela inteira — a tabela diz o que existe, as listas dizem a quem ligar.
  const comAtencao = pares
    .map(({ l, regra }) => ({ l, a: atencaoDe(regra, agora) }))
    .filter((x) => x.a !== null);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Numero rotulo="Vendidas" valor={r.vendidas} nota="e não canceladas" />
        <Numero rotulo="Vivas" valor={r.vivas} nota="falaram com o hub" />
        <Numero rotulo="Nunca instalaram" valor={r.nuncaFalaram} />
        <Numero
          rotulo="Paga e não usa"
          valor={r.pagaENaoUsa}
          tom="alerta"
          nota="cancelamento chegando"
        />
        <Numero
          rotulo="Usa e não paga"
          valor={r.usaENaoPaga}
          tom="alerta"
          nota="cobrança a fazer"
        />
      </div>

      {comAtencao.length > 0 ? (
        <div>
          <h3 className="mb-2 font-medium">A quem ligar</h3>
          <div className="flex flex-col gap-2">
            {comAtencao.map(({ l, a }) => (
              <Cartao
                key={l.id}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">{l.customer ?? "Sem nome"}</p>
                  <p className="mt-0.5 text-text-muted text-xs">
                    {nomeDaFaixa(l.product, faixaDaCota(l.tenant_limit))} ·{" "}
                    <Sinal ultimo={l.last_seen_at} agora={agora} />
                  </p>
                </div>
                <span className="text-warning text-xs">
                  {a === "paga-e-nao-usa" ? "paga e não usa" : "usa e não paga"}
                </span>
              </Cartao>
            ))}
          </div>
        </div>
      ) : null}

      <div>
        <h3 className="mb-2 font-medium">Todas as licenças</h3>
        {/* A grade some no celular e cada licenca vira bloco: cinco colunas em 375 px espremem o
            nome do cliente ate ficar ilegivel, que e o unico campo que nao pode encolher. */}
        <div className="flex flex-col gap-2">
          <div className="hidden grid-cols-[minmax(0,2.5fr)_minmax(0,1fr)_9rem_6.5rem_minmax(0,1.4fr)] items-center gap-4 px-5 text-text-muted text-xs sm:grid">
            <span>Cliente</span>
            <span>Faixa</span>
            <span>Venda</span>
            <span>Contrato</span>
            <span>Sinal</span>
          </div>
          {licencas.map((l) => (
            <Cartao
              key={l.id}
              className="flex flex-col items-start gap-2 sm:grid sm:grid-cols-[minmax(0,2.5fr)_minmax(0,1fr)_9rem_6.5rem_minmax(0,1.4fr)] sm:items-center sm:gap-4"
            >
              <span className="truncate text-sm">{l.customer ?? "Sem nome"}</span>
              <span className="text-text-muted text-xs">
                {nomeDaFaixa(l.product, faixaDaCota(l.tenant_limit))}
              </span>
              <MudarVenda licenca={l} aoMudar={aoMudar} />
              {/* 🔴 `justify-self-start`: um <span> filho direto de grade e blocado e ESTICA ate a
                  largura da coluna, entao o selo saia com o dobro do tamanho do ON ao lado. */}
              <span className="justify-self-start">
                <Selo estado={l.status} />
              </span>
              <span className="text-xs">
                <Sinal ultimo={l.last_seen_at} agora={agora} />
              </span>
            </Cartao>
          ))}
        </div>
      </div>
    </>
  );
}

// As páginas do console.
type Pagina = "licencas" | "crm" | "configuracoes";

const MENU: ReadonlyArray<ItemDeMenu<Pagina>> = [
  { id: "licencas", rotulo: "Licenças", Icone: KeyRound },
  { id: "crm", rotulo: "Clientes", Icone: Users },
  { id: "configuracoes", rotulo: "Configurações", Icone: Settings },
];

// Antes isto era um expansor no pé da coluna, com a SUA PRÓPRIA cópia dos efeitos de tema — a
// terceira no projeto. Virando página, a preferência desce de quem já governa o tema (`useTema` na
// raiz), e some a chance de as cópias discordarem.
function PaginaConfiguracoes({
  preferencia,
  escolher,
}: {
  preferencia: Preferencia;
  escolher: (p: Preferencia) => void;
}) {
  return (
    <Cartao className="flex flex-col gap-3">
      <Campo
        rotulo="Tema"
        dica="Automático acompanha o sistema, e muda junto com ele."
      >
        <select
          value={preferencia}
          onChange={(e) => escolher(e.target.value as Preferencia)}
          className={classeEntrada}
        >
          <option value="auto">Automático</option>
          <option value="light">Claro</option>
          <option value="dark">Escuro</option>
        </select>
      </Campo>
    </Cartao>
  );
}

export function App() {
  const [dentro, setDentro] = useState<boolean | null>(null);
  const [licencas, setLicencas] = useState<Licenca[] | null>(null);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [versaoAtual, setVersaoAtual] = useState<string | null>(null);
  const [pagina, setPagina] = useState<Pagina>("licencas");
  const [preferencia, escolherTema, tema] = useTema();

  const carregar = useCallback(async () => {
    try {
      const lista = await api.licencas();
      setLicencas(lista);
      setDentro(true);
    } catch (e) {
      if (e instanceof NaoAutorizado) setDentro(false);
      else throw e;
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Qual é a versão corrente do produto. Vem do mesmo arquivo que a instalação lê para decidir se
  // mostra o aviso de atualização, então o console e o cliente concordam por construção.
  useEffect(() => {
    fetch("https://pro.indicafacil.ai/latest-version.json")
      .then((r) => r.json())
      .then((d: { latestVersion?: string }) => setVersaoAtual(d.latestVersion ?? null))
      // Um hub sem internet continua operável: o selo de atraso some, e mais nada.
      .catch(() => setVersaoAtual(null));
  }, []);

  if (dentro === null) return <div className="p-6 text-sm text-text-muted">Carregando…</div>;
  if (!dentro) return <Entrar aoEntrar={() => void carregar()} tema={tema} />;

  const atual = licencas?.find((l) => l.id === escolhida) ?? null;

  const lista = (
    // 🔴 `md:flex-row`, e não `flex` puro como antes: a coluna de 20rem ao lado do detalhe não cabe
    // num celular, e o que acontecia lá era o detalhe espremido a quase nada. Empilhado, a lista
    // vem primeiro e o detalhe desce — a mesma regra do menu da casca.
    <div className="flex flex-col gap-6 md:flex-row">
      <section className="flex w-full shrink-0 flex-col gap-3 md:w-80">
        <CriarLicenca aoCriar={() => void carregar()} />
        <div className="flex flex-col gap-2">
          {licencas?.length === 0 ? (
            <Vazio>Nenhuma licença ainda.</Vazio>
          ) : (
            licencas?.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setEscolhida(l.id)}
                aria-current={l.id === escolhida ? "true" : undefined}
                className={cn(
                  "rounded-xl border p-3 text-left transition-colors",
                  l.id === escolhida
                    ? "border-accent bg-bg-tertiary"
                    : "border-border bg-bg-secondary hover:border-border-hover",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm">{l.customer ?? "Sem nome"}</span>
                  <Selo estado={l.status} />
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-text-muted">
                  <span>
                    {nomeDaFaixa(l.product, faixaDaCota(l.tenant_limit)).toLowerCase()}
                  </span>
                  <span>·</span>
                  <span>
                    {l.installations} instalaç{l.installations === 1 ? "ão" : "ões"}
                  </span>
                  {/* Atrasada quando QUALQUER instalação está para trás, não quando a mais nova
                      está: um cliente com duas instalações, uma em dia e outra velha, precisa
                      aparecer — era justamente ele que o `max()` escondia. */}
                  {l.versions.some((v) => estaAtrasada(v, versaoAtual)) ? (
                    <span className="text-warning">· atrasada</span>
                  ) : null}
                </div>
              </button>
            ))
          )}
        </div>
      </section>

      {/* `div`, não `main`: a casca já abriu um `<main>` acima, e dois na mesma página não é HTML
          válido — o leitor de tela perde qual é o conteúdo principal. */}
      <div className="min-w-0 flex-1">
        {atual ? (
          <Detalhe licenca={atual} versaoAtual={versaoAtual} aoMudar={() => void carregar()} />
        ) : (
          <Vazio>Escolha uma licença à esquerda, ou crie a primeira.</Vazio>
        )}
      </div>
    </div>
  );

  return (
    <Casca
      tema={tema}
      menu={MENU}
      pagina={pagina}
      irPara={setPagina}
      larguraDoConteudo="max-w-6xl"
      acoes={
        versaoAtual ? (
          <span className="text-xs text-text-muted">
            produto em {paraMostrar(versaoAtual)}
          </span>
        ) : null
      }
    >
      {pagina === "configuracoes" ? (
        <PaginaConfiguracoes preferencia={preferencia} escolher={escolherTema} />
      ) : pagina === "crm" ? (
        <PaginaCRM licencas={licencas} aoMudar={() => void carregar()} />
      ) : (
        lista
      )}
    </Casca>
  );
}
